import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, getActiveBranchName } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PrStatusBadge } from "@/components/returns/PrStatusBadge";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";
import { money, PR_STATUSES } from "@/lib/constants";
import { Search, Filter, PackageX } from "lucide-react";

export default function Devoluciones() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches } = useTenant();
  const navigate = useNavigate();
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const params = {
    ...tenantParam,
    search: search || undefined,
    status: filterStatus !== "all" ? filterStatus : undefined,
    branch_id: filterBranch !== "all" ? filterBranch : undefined,
    supplier_id: filterSupplier !== "all" ? filterSupplier : undefined,
    date_from: filterDateFrom || undefined,
    date_to: filterDateTo || undefined,
  };

  async function fetchList() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data }, { data: sup }] = await Promise.all([
        api.get("/purchase-returns", { params }),
        api.get("/suppliers", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(data);
      setSuppliers(sup);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search, filterStatus, filterBranch, filterSupplier,
    filterDateFrom, filterDateTo, activeTenantId, isSuperAdmin,
  ]);

  return (
    <div className="space-y-6" data-testid="page-devoluciones">
      <PageHeader
        title="Devoluciones a proveedor"
        description="Devoluciones sobre líneas inventariables. Al emitir, se genera movimiento inverso de kardex y (si es seguro) se ajusta la cuenta por pagar."
        actions={
          <ShareExportPanel
            reportKey="purchase_returns"
            reportName="Devoluciones a proveedor"
            endpoint="/purchase-returns"
            params={params}
            exportPermission="purchase_returns:export"
            filename="devoluciones-a-proveedor"
            branchId={filterBranch !== "all" ? filterBranch : null}
            filters={{
              date_from: filterDateFrom || null,
              date_to: filterDateTo || null,
              status: filterStatus !== "all" ? filterStatus : null,
              branch_name: getActiveBranchName(),
            }}
            stats={{
              returns_count: items.length,
              total_returned: items.reduce((s, r) => s + Number(r.subtotal || 0), 0),
              ap_adjustment_total: items.reduce((s, r) => s + Number(r.ap_adjustment_amount || 0), 0),
              row_count: items.length,
            }}
            testIdPrefix="pr-share"
          />
        }
      />

      <div className="bg-white border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Filter className="h-3.5 w-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar folio DEV…"
              className="pl-9 h-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="pr-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="pr-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estatus</SelectItem>
              {PR_STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger data-testid="pr-filter-branch"><SelectValue placeholder="Sucursal" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sucursal activa</SelectItem>
              {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterSupplier} onValueChange={setFilterSupplier}>
            <SelectTrigger data-testid="pr-filter-supplier"><SelectValue placeholder="Proveedor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.trade_name || s.legal_name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2 lg:col-span-2">
            <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} data-testid="pr-filter-date-from" />
            <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} data-testid="pr-filter-date-to" />
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground" data-testid="pr-count">
        {items.length} {items.length === 1 ? "devolución" : "devoluciones"}
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Sin devoluciones"
            description="Las devoluciones se registran desde el detalle de la OC (botón 'Registrar devolución')."
            icon={PackageX}
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Folio</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Folio OC</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Ajuste c/IVA</TableHead>
                <TableHead>CxP</TableHead>
                <TableHead>Estatus</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((pr) => (
                <TableRow
                  key={pr.id}
                  className="tbl-row cursor-pointer"
                  onClick={() => navigate(`/devoluciones/${pr.id}`)}
                  data-testid={`row-pr-${pr.id}`}
                >
                  <TableCell className="mono font-medium text-tenant">{pr.folio}</TableCell>
                  <TableCell className="text-sm">{pr.return_date}</TableCell>
                  <TableCell className="mono text-xs">{pr.po_folio}</TableCell>
                  <TableCell className="text-sm">{pr.supplier_name || "—"}</TableCell>
                  <TableCell className="text-sm">{pr.branch_name || "—"}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(pr.subtotal)}</TableCell>
                  <TableCell className="text-right mono text-sm">{money(pr.ap_adjustment_amount)}</TableCell>
                  <TableCell className="text-xs">
                    {pr.status === "emitida" ? (
                      pr.ap_adjusted
                        ? <span className="text-emerald-700">Ajustada</span>
                        : <span className="text-amber-700">Pendiente</span>
                    ) : "—"}
                  </TableCell>
                  <TableCell><PrStatusBadge status={pr.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
